import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Mail, FileText, Link as LinkIcon, Copyright, ScrollText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import styles from './ImpressumPage.module.css';

export const ImpressumPage: React.FC = () => {
    const { language } = useLanguage();

    const content = {
        de: {
            title: 'Impressum',
            subtitle: 'der Integrova AI GmbH'
        },
        en: {
            title: 'Legal Notice',
            subtitle: 'of Integrova AI GmbH'
        }
    };

    const t = content[language];

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.hero__container}>
                    <motion.div
                        className={styles.hero__icon}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200 }}
                    >
                        <Building2 size={40} />
                    </motion.div>
                    <motion.h1
                        className={styles.hero__title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15 }}
                    >
                        {t.title}
                    </motion.h1>
                    <motion.p
                        className={styles.hero__subtitle}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0 }}
                    >
                        {t.subtitle}
                    </motion.p>
                </div>
            </section>

            <section className={styles.content}>
                <div className={styles.container}>
                    {/* Company Info */}
                    <motion.article
                        className={styles.section}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.15, delay: 0 }}
                    >
                        <div className={styles.sectionContent}>
                            <div className={styles.companyCard}>
                                <p><strong>Integrova AI GmbH</strong></p>
                                <p>Garmischer Str. 195</p>
                                <p>81377 München</p>
                                <p>Deutschland</p>
                                <br />
                                <p><strong>vertreten durch</strong></p>
                                <p>André Tietje</p>
                                <br />
                                <p><strong>Registergericht:</strong> Amtsgericht München</p>
                                <p><strong>HRB:</strong> 308289</p>
                            </div>
                        </div>
                    </motion.article>

                    {/* Contact */}
                    <motion.article
                        className={styles.section}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.15 }}
                    >
                        <div className={styles.sectionHeader}>
                            <Mail size={24} />
                            <h2>Kontakt</h2>
                        </div>
                        <div className={styles.sectionContent}>
                            <p><a href="mailto:info@integrovaai.de">info@integrovaai.de</a></p>
                        </div>
                    </motion.article>

                    {/* § 1 Inhaltshinweis */}
                    <motion.article
                        className={styles.section}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.15 }}
                    >
                        <div className={styles.sectionHeader}>
                            <FileText size={24} />
                            <h2>§ 1 Inhaltshinweis</h2>
                        </div>
                        <div className={styles.sectionContent}>
                            <p>Die kostenlosen und frei zugänglichen Inhalte dieser Website wurden mit größtmöglicher Sorgfalt erstellt. Der Anbieter dieser Website übernimmt jedoch keine Gewähr für die Richtigkeit, Vollständigkeit und Aktualität der bereitgestellten fachlichen Informationen, Analysen und allgemeinen Inhalte.</p>
                            <p>Namentlich gekennzeichnete Beiträge geben die Meinung des jeweiligen Autors wieder und nicht zwingend die Meinung des Anbieters.</p>
                            <p>Allein durch den Aufruf der kostenlosen und frei zugänglichen Inhalte kommt kein Vertragsverhältnis zwischen dem Nutzer und dem Anbieter zustande; insoweit fehlt es am Rechtsbindungswillen des Anbieters.</p>
                        </div>
                    </motion.article>

                    {/* § 2 Externe Links */}
                    <motion.article
                        className={styles.section}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.15 }}
                    >
                        <div className={styles.sectionHeader}>
                            <LinkIcon size={24} />
                            <h2>§ 2 Externe Links</h2>
                        </div>
                        <div className={styles.sectionContent}>
                            <p>Diese Website enthält Verknüpfungen zu Websites Dritter („externe Links"). Diese Websites unterliegen der Haftung der jeweiligen Betreiber.</p>
                            <p>Der Anbieter hat bei der erstmaligen Verknüpfung der externen Links die fremden Inhalte daraufhin überprüft, ob etwaige Rechtsverstöße bestehen. Zu dem Zeitpunkt waren keine Rechtsverstöße ersichtlich.</p>
                            <p>Der Anbieter hat keinerlei Einfluss auf die aktuelle und zukünftige Gestaltung sowie auf die Inhalte der verknüpften Seiten. Das Setzen von externen Links bedeutet nicht, dass sich der Anbieter die hinter dem Verweis oder Link liegenden Inhalte zu Eigen macht.</p>
                            <p>Eine ständige Kontrolle der externen Links ist ohne konkrete Hinweise auf Rechtsverstöße nicht zumutbar. Bei Bekanntwerden von Rechtsverstößen werden derartige externe Links unverzüglich entfernt.</p>
                        </div>
                    </motion.article>

                    {/* § 3 Urheber- und Leistungsschutzrechte */}
                    <motion.article
                        className={styles.section}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.15 }}
                    >
                        <div className={styles.sectionHeader}>
                            <Copyright size={24} />
                            <h2>§ 3 Urheber- und Leistungsschutzrechte</h2>
                        </div>
                        <div className={styles.sectionContent}>
                            <p>Die auf dieser Website veröffentlichten Inhalte unterliegen dem deutschen Urheber- und Leistungsschutzrecht.</p>
                            <p>Jede vom deutschen Urheber- und Leistungsschutzrecht nicht zugelassene Verwertung bedarf der vorherigen schriftlichen Zustimmung des Anbieters oder des jeweiligen Rechteinhabers. Dies gilt insbesondere für Vervielfältigung, Bearbeitung, Übersetzung, Einspeicherung, Verarbeitung oder Wiedergabe von Inhalten in Datenbanken oder anderen elektronischen Medien und Systemen.</p>
                            <p>Inhalte und Rechte Dritter sind als solche gekennzeichnet. Die unerlaubte Vervielfältigung oder Weitergabe einzelner Inhalte oder kompletter Seiten ist nicht gestattet und kann rechtliche Konsequenzen nach sich ziehen.</p>
                            <p>Die Herstellung von Kopien und Downloads ist ausschließlich für den persönlichen, privaten und nicht kommerziellen Gebrauch erlaubt.</p>
                            <p>Die Darstellung dieser Website in fremden Frames ist nur mit vorheriger schriftlicher Erlaubnis zulässig.</p>
                        </div>
                    </motion.article>

                    {/* § 4 Besondere Nutzungsbedingungen */}
                    <motion.article
                        className={styles.section}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.15 }}
                    >
                        <div className={styles.sectionHeader}>
                            <ScrollText size={24} />
                            <h2>§ 4 Besondere Nutzungsbedingungen</h2>
                        </div>
                        <div className={styles.sectionContent}>
                            <p>Soweit für einzelne Nutzungen dieser Website besondere Bedingungen gelten, die von den vorgenannten Paragraphen abweichen, wird an entsprechender Stelle ausdrücklich darauf hingewiesen. In diesem Fall gelten im jeweiligen Einzelfall die besonderen Nutzungsbedingungen.</p>
                        </div>
                    </motion.article>
                </div>
            </section>
        </div>
    );
};

export default ImpressumPage;
